
/**
 * The Directory's tabs, and who is on each.
 *
 *   all     everyone in the company — the default, because "who works here" is the question
 *           the page is named for, and the office is part of the answer
 *   normal  the crew, with their crews as the Team board has them
 *   cat     the crew, with their crews as the Group board has them
 *   office  the PMs and PMAs
 *
 * Kept out of People.jsx because that page reads useAuth and is not rendered in the tests;
 * these rules decide who a scheduler sees, and deserve to be checkable on their own.
 */

export const TABS = ['all', 'normal', 'cat', 'office']

// The roles the PMs & PMAs tab is for.
export const OFFICE_ROLES = ['pm', 'pma']

/**
 * The tab an address asks for. `mode=normal|cat` is how a link said it before there were
 * tabs, and still lands on the same crew view. Anything else — nothing, or a value somebody
 * typed — is All: validated rather than trusted, like every other key the page reads.
 */
export function readTab(params) {
  const asked = params.get('tab') || params.get('mode')
  return TABS.includes(asked) && asked !== 'all' ? asked : 'all'
}

const rolesOf = (person) => person?.roles || []

/** Whether a person belongs on a tab. */
export function onTab(person, tab) {
  if (tab === 'all') return true
  if (tab === 'office') return rolesOf(person).some((r) => OFFICE_ROLES.includes(r))
  return Boolean(person.fieldStaff)
}

// What somebody does before what they may do: "PMA, Group Mode Admin" rather than the other
// way round, because the job is what a person looking somebody up is asking about.
const SUMMARY_ORDER = ['owner', 'pm', 'pma', 'team_admin', 'group_admin', 'asset_admin']

/**
 * "PM", "PMA, Group Mode Admin" — what somebody does, for a person who is not crew or on the
 * PMs & PMAs tab, where a crew name would be the wrong answer. Without Field staff, which the
 * tab or the crew beside it already says.
 */
export function roleSummary(person, roleLabel) {
  const held = SUMMARY_ORDER.filter((r) => rolesOf(person).includes(r))
  return held.map((r) => roleLabel(r)).join(', ') || 'No role'
}

/**
 * The board a time-off write names. Time off has no mode of its own — the API asks for one
 * only to check the writer may write that board — so this is the board on screen when the
 * writer may write it, and otherwise one they may. Sending the live board regardless refused
 * a PM recording a PMA's leave while Group mode was running, with no tab to escape to.
 */
export function timeOffModeFor(writeBoard, onScreen) {
  if (writeBoard?.[onScreen]) return onScreen
  return ['normal', 'cat'].find((m) => writeBoard?.[m]) || onScreen
}

/**
 * The crew and everybody else, as the one list the page shows, by name. The server sends
 * them apart (`staff` is what the board — and Claude's list_people — have always read), and
 * only the crew carry job types.
 */
export function directoryPeople(data) {
  return [
    ...(data?.staff || []).map((s) => ({ ...s, fieldStaff: true })),
    ...(data?.office || []).map((s) => ({ ...s, fieldStaff: false, jobTypeIds: [] })),
  ].sort((a, b) => a.name.localeCompare(b.name))
}
